import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs';
import * as LogAnalysisActions from './log-analysis.actions';
import { FileParserService } from '../../../shared/file-parser/file-parser.service';
import { MatomoService } from '../../../shared/feature-usage-tracker/matomo.service';

@Injectable()
export class LogAnalysisEffects {
  private readonly action$ = inject(Actions);
  private readonly fileParserService = inject(FileParserService);
  private readonly matomoService = inject(MatomoService);

  public onTriggerLogAnalysis$ = createEffect(() => {
    return this.action$.pipe(
      ofType(LogAnalysisActions.loadLogAnalysis),
      mergeMap((action) => {
        return this.fileParserService.getLogAnalysisResult(action.pipelineRunId).pipe(
          map((result) => {
            return LogAnalysisActions.setLogAnalysisResult({
              pipelineRunId: action.pipelineRunId,
              aiLogAnalysis: result,
            });
          }),
          catchError((error) => {
            if (error.status === 404) {
              this.matomoService.trackEvent('log-ai-analysis', 'log-ai-analysis-not-found-error');
              return of(LogAnalysisActions.loadLogAnalysisNotFound({ pipelineRunId: action.pipelineRunId }));
            }
            if (error.status === 503) {
              // temporary unavailable
              this.matomoService.trackEvent('log-ai-analysis', 'log-ai-analysis-service-unavailable-error');
              return of(LogAnalysisActions.loadLogAnalysisTemporaryError({ pipelineRunId: action.pipelineRunId }));
            }
            if (error.status === 500) {
              // unknown server error or error accessing the AI
              this.matomoService.trackEvent('log-ai-analysis', 'log-ai-analysis-internal-server-error');
              return of(LogAnalysisActions.loadLogAnalysisError({ pipelineRunId: action.pipelineRunId }));
            }
            this.matomoService.trackEvent('log-ai-analysis', 'log-ai-analysis-default-error');
            return of(LogAnalysisActions.loadLogAnalysisError({ pipelineRunId: action.pipelineRunId }));
          }),
          takeUntil(
            this.action$.pipe(
              ofType(LogAnalysisActions.loadLogAnalysisError),
              filter((newAction) => newAction.pipelineRunId === action.pipelineRunId),
            ),
          ),
        );
      }),
    );
  });
}
